import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  FiSave, FiRefreshCw, FiMonitor, FiSmartphone, FiType,
  FiImage, FiLayout, FiGrid, FiStar, FiAlignLeft, FiLink,
  FiChevronRight, FiChevronUp, FiChevronDown,
  FiPlus, FiTrash2, FiX, FiEdit2, FiArrowLeft, FiEye, FiEyeOff, FiSearch,
} from 'react-icons/fi';
import { adminApi } from '../adminApi';
import ImageUpload from '../components/ImageUpload';
import { showDelete, showAlert } from '../utils/dialog';
import '../admin.css';
import './AdminStore.css';

// Order matches top-to-bottom on homepage
const SECTIONS = [
  { id: 'ticker',             label: 'Announcement Banner',  icon: <FiImage size={16} />,  desc: 'Full-width image strip at top' },
  { id: 'logo',               label: 'Logo',                 icon: <FiType size={16} />,   desc: 'Store name & tagline' },
  { id: 'social',             label: 'Social Links',         icon: <FiLink size={16} />,   desc: 'Facebook & Instagram URLs' },
  { id: 'banners',            label: 'Banners',              icon: <FiImage size={16} />,  desc: 'Homepage image slideshow' },
  { id: 'popularCategories', label: 'Popular Categories',   icon: <FiGrid size={16} />,   desc: 'Category cards below banner' },
  { id: 'homepageCategories', label: 'Homepage Categories', icon: <FiGrid size={16} />,   desc: 'Category product sections on homepage' },
];

const DEFAULT_SETTINGS = {
  logo:       { text: 'Store', dot: '', tagline: '', color: '#17140F', accent_color: '#234D3B', image_url: '' },
  topBanner:  { enabled: false, image_url: '', link: '', height: 36 },
  popularCategories: [
    { name: 'Electronics',  image_url: 'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=600&h=600&fit=crop', link: '/products' },
    { name: 'Fashion',      image_url: 'https://images.unsplash.com/photo-1445205170230-053b83016050?w=600&h=600&fit=crop', link: '/products' },
    { name: 'Home & Living', image_url: 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=600&h=600&fit=crop', link: '/products' },
    { name: 'Beauty',       image_url: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=600&h=600&fit=crop', link: '/products' },
  ],
  categoryStrip: [],
  homepageCategories: [{ type: 'new_arrivals' }],
  social:     { facebook: '', instagram: '', instagram_handle: '' },
  ticker:     { enabled: true, messages: ['Free Shipping on orders over Rs 2000', 'New Arrivals Every Week', 'Secure & Encrypted Checkout', 'Free 30-Day Returns'], bg_color: '#f0b429', text_color: '#0f1f3d' },
  hero:       { tag: '✦ New arrivals every week', title: 'Shop the <em>future</em> of online retail', subtitle: 'Discover curated collections across electronics, fashion, and home, all in one place, delivered fast.', btn1_text: 'Shop Now', btn1_link: '/products', btn2_text: 'Browse Categories', btn2_link: '/products', stat1: { value: '50k+', label: 'Products' }, stat2: { value: '200k+', label: 'Customers' }, stat3: { value: '4.9★', label: 'Avg Rating' } },
  categories: { enabled: true, label: 'Collections', title: 'Shop by Category', subtitle: 'Explore our handpicked selections, with new categories added every month' },
  midBanner: { enabled: true, badge: 'New Arrivals', heading: 'Quality You Can Trust', subtitle: 'Everything you need, all in one place, delivered fast to your door', btn_text: 'Shop the Collection', btn_link: '/products', image_url: '', bg_color: '#0f1f3d' },
  featured:   { enabled: true, label: 'Featured', title: 'Trending This Week', subtitle: 'Our most popular products, loved by thousands of customers' },
  staffFavorites: { enabled: true, title: 'Staff Favorites', subtitle: 'Most liked by us', products: [] },
  newArrivals:    { enabled: true, title: 'New Arrivals', subtitle: 'Discover something New', products: [] },
  featuredCats: [],
  carouselCats: [],
  navLinks:   [
    { label: 'Home',         to: '/'              },
    { label: 'New Arrivals', to: '/new-arrivals'  },
    { label: 'Deals',        to: '/deals'         },
    { label: 'Most Selling', to: '/most-selling'  },
    { label: 'About Us',     to: '/about'         },
    { label: 'Contact',      to: '/contact'       },
  ],
};

// ── Field helpers ───────────────────────────────────────────────
function Field({ label, children, hint }) {
  return (
    <div className="admin-form-group">
      <label>{label}</label>
      {children}
      {hint && <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
function Input({ value, onChange, ...rest }) {
  return <input value={value ?? ''} onChange={e => onChange(e.target.value)} {...rest} />;
}
function ColorRow({ label, value, onChange }) {
  return (
    <Field label={label}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)}
          style={{ width: 40, height: 36, padding: 2, border: '1.5px solid var(--border)', borderRadius: 8, cursor: 'pointer' }} />
        <input value={value || ''} onChange={e => onChange(e.target.value)}
          style={{ flex: 1, padding: '10px 13px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '.9rem', fontFamily: 'inherit', outline: 'none' }} />
      </div>
    </Field>
  );
}
function Toggle({ label, checked, onChange, desc }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
      <label className="store-toggle" style={{ flexShrink: 0 }}>
        <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} />
        <span className="store-toggle-track" />
      </label>
      <div>
        <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{label}</div>
        {desc && <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{desc}</div>}
      </div>
    </div>
  );
}

// ── Banner CRUD Panel ───────────────────────────────────────────
const EMPTY_BANNER = { title: '', subtitle: '', image_url: '', btn_text: '', btn_link: '/products', bg_color: '#1e1b4b', text_color: '#ffffff', is_active: true, sort_order: 0 };

function BannerForm({ banner, onSave, onCancel, saving }) {
  const [form, setForm] = useState({ ...EMPTY_BANNER, ...banner });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
      <button className="btn-admin btn-admin-ghost btn-admin-sm" onClick={onCancel} style={{ marginBottom: 14, gap: 6 }}>
        <FiArrowLeft size={12} /> Back to banners
      </button>
      <div className="edit-panel-section-title"><FiImage size={14} />{form.id ? 'Edit Banner' : 'New Banner'}</div>

      <ImageUpload
        label="Banner Image *"
        value={form.image_url}
        onChange={v => set('image_url', v)}
        hint="Recommended: 1250 × 600 px · JPG or WebP for best quality"
        type="banner"
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn-admin btn-admin-ghost" onClick={onCancel} style={{ flex: 1 }}>Cancel</button>
        <button className="btn-admin btn-admin-primary" onClick={() => onSave(form)} disabled={saving || !form.image_url.trim()} style={{ flex: 1 }}>
          {saving ? 'Saving…' : 'Save Banner'}
        </button>
      </div>
    </div>
  );
}

function BannersPanel({ refreshIframe }) {
  const [banners, setBanners]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(null);
  const [saving,  setSaving]      = useState(false);
  const [error,   setError]       = useState('');
  const [dragOver, setDragOver]   = useState(null);
  const dragSrcIdx                = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.getBanners()
      .then(r => setBanners(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    setSaving(true);
    try {
      if (form.id) await adminApi.updateBanner(form.id, form);
      else         await adminApi.createBanner(form);
      setEditing(null); setError('');
      load();
      refreshIframe();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save banner.');
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!await showDelete('Delete this banner?')) return;
    await adminApi.deleteBanner(id);
    load();
    refreshIframe();
  };

  const toggle = async (b) => {
    await adminApi.updateBanner(b.id, { is_active: !b.is_active });
    load();
    refreshIframe();
  };

  const reorder = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null) return;
    const reordered = [...banners];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setBanners(reordered);
    await Promise.all(reordered.map((b, i) => adminApi.updateBanner(b.id, { sort_order: i })));
    refreshIframe();
  };

  if (editing !== null) {
    return <BannerForm banner={editing} onSave={save} onCancel={() => setEditing(null)} saving={saving} />;
  }

  return (
    <div>
      {error && <div className="admin-error" style={{ marginBottom: 12 }}>{error}</div>}
      <div className="edit-panel-section-title"><FiImage size={14} /> Homepage Slideshow</div>
      <p style={{ fontSize: '.8125rem', color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
        Add full-width banner images. They slide automatically on the homepage.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '8px 12px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 8 }}>
        <FiImage size={13} style={{ color: '#f59e0b', flexShrink: 0 }} />
        <span style={{ fontSize: '.78rem', color: '#92400e', fontWeight: 600 }}>
          Recommended size: <strong>1250 × 600 px</strong> &nbsp;·&nbsp; JPG or WebP
        </span>
      </div>

      <button
        className="btn-admin btn-admin-primary"
        style={{ width: '100%', justifyContent: 'center', marginBottom: 16 }}
        onClick={() => setEditing({ ...EMPTY_BANNER })}
      >
        <FiPlus size={14} /> Add Banner Image
      </button>

      {loading ? (
        <div className="admin-loading" style={{ padding: 20 }}><div className="admin-spinner" /></div>
      ) : banners.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px 0', fontSize: '.875rem' }}>
          No banners yet. Add your first image!
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {banners.map((b, idx) => (
            <div
              key={b.id}
              className={`banner-list-item${dragOver === idx ? ' drag-over' : ''}`}
              draggable
              onDragStart={() => { dragSrcIdx.current = idx; }}
              onDragOver={e => { e.preventDefault(); setDragOver(idx); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={() => { reorder(dragSrcIdx.current, idx); setDragOver(null); }}
              onDragEnd={() => { setDragOver(null); dragSrcIdx.current = null; }}
            >
              <div className="banner-drag-handle" title="Drag to reorder">
                <span /><span /><span /><span /><span /><span />
              </div>
              <div className="banner-list-thumb" style={{ backgroundColor: b.bg_color || '#1e1b4b' }}>
                {b.image_url
                  ? <img src={b.image_url} alt={b.title} onError={e => { e.target.style.display='none'; }} />
                  : <FiImage size={20} style={{ color: 'rgba(255,255,255,0.5)' }} />
                }
                <span className="banner-list-num">{idx + 1}</span>
              </div>
              <div className="banner-list-info">
                <div className="banner-list-title">{b.title || <em style={{ color: 'var(--text-muted)' }}>No title</em>}</div>
                <div className="banner-list-url">{b.image_url ? b.image_url.split('/').pop() : 'No image'}</div>
              </div>
              <div className="banner-list-actions">
                <button
                  className="banner-icon-btn"
                  onClick={() => toggle(b)}
                  title={b.is_active ? 'Deactivate' : 'Activate'}
                  style={{ color: b.is_active ? 'var(--success)' : 'var(--text-muted)' }}
                >
                  {b.is_active ? <FiEye size={14} /> : <FiEyeOff size={14} />}
                </button>
                <button className="banner-icon-btn" onClick={() => setEditing({ ...b })} title="Edit">
                  <FiEdit2 size={14} />
                </button>
                <button className="banner-icon-btn danger" onClick={() => del(b.id)} title="Delete">
                  <FiTrash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Section Edit Panels ─────────────────────────────────────────

function SocialPanel({ s, set, settings, setSettings }) {
  return (
    <div>
      <div className="edit-panel-section-title"><FiLink size={14} /> Social Links</div>
      <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 18 }}>
        These links open when visitors click the Facebook / Instagram icons in the header.
      </p>
      <Field label="WhatsApp Number" hint="Floating chat button on the frontend — include country code without + (e.g. 923001234567)">
        <Input value={settings.whatsapp_number || ''} onChange={v => setSettings(prev => ({ ...prev, whatsapp_number: v }))} placeholder="923001234567" />
      </Field>
      <Field label="Instagram Handle" hint="Displayed in the Follow Us section on the homepage (e.g. @yourstore)">
        <Input value={s.instagram_handle} onChange={v => set('instagram_handle', v)} placeholder="@yourstore" />
      </Field>
      <Field label="Instagram URL" hint="Link when visitors click the Instagram icon or Follow button">
        <Input value={s.instagram} onChange={v => set('instagram', v)} placeholder="https://instagram.com/yourhandle" />
      </Field>
      <Field label="Facebook URL" hint="Link when visitors click the Facebook icon in the header">
        <Input value={s.facebook} onChange={v => set('facebook', v)} placeholder="https://facebook.com/yourpage" />
      </Field>
    </div>
  );
}

function LogoPanel({ s, set }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminApi.uploadImage(file);
      set('image_url', res.data.url);
    } catch { showAlert('Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <div className="edit-panel-section-title"><FiType size={14} /> Logo & Branding</div>

      <Field label="Logo Image" hint="Upload an image logo — replaces the text logo if set">
        {s.image_url && (
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={s.image_url} alt="Logo preview" style={{ height: 48, maxWidth: 180, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 6, padding: 4, background: '#f8fafc' }} />
            <button type="button" className="btn-admin btn-admin-danger btn-admin-sm" onClick={() => set('image_url', '')}>
              <FiX size={12} /> Remove
            </button>
          </div>
        )}
        <input type="file" accept="image/*" onChange={upload} disabled={uploading} />
        {uploading && <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}> Uploading…</span>}
        <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Recommended: PNG with transparent background · Max height 48px</div>
      </Field>

      <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0', opacity: 0.5 }} />
      <p style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: 10 }}>Text logo (used when no image is set)</p>

      <Field label="Store Name" hint="First word of the logo (e.g. Acme)">
        <Input value={s.text} onChange={v => set('text', v)} placeholder="Store" />
      </Field>
      <Field label="Accent Word" hint="Second word shown in accent color (e.g. Store)">
        <Input value={s.dot} onChange={v => set('dot', v)} placeholder="" />
      </Field>
      <Field label="Tagline" hint="Small text below the logo (optional)">
        <Input value={s.tagline} onChange={v => set('tagline', v)} placeholder="Quality products, delivered fast" />
      </Field>
      <div className="admin-form-row" style={{ gap: 16, marginTop: 8 }}>
        <Field label="Name Color">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="color" value={s.color || '#111111'} onChange={e => set('color', e.target.value)} style={{ width: 40, height: 36, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', padding: 2 }} />
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{s.color || '#111111'}</span>
          </div>
        </Field>
        <Field label="Accent Color">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="color" value={s.accent_color || '#c0392b'} onChange={e => set('accent_color', e.target.value)} style={{ width: 40, height: 36, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', padding: 2 }} />
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{s.accent_color || '#c0392b'}</span>
          </div>
        </Field>
      </div>
    </div>
  );
}

function TopBannerPanel({ s, set }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminApi.uploadImage(file);
      set('image_url', res.data.url);
    } catch { showAlert('Upload failed'); }
    finally { setUploading(false); }
  };

  const height = s.height || 70;

  return (
    <div>
      <div className="edit-panel-section-title"><FiImage size={14} /> Top Banner Image</div>
      <Toggle label="Show Top Banner" checked={s.enabled !== false} onChange={v => set('enabled', v)}
        desc="Full-width banner image shown at the very top of the page" />

      <div style={{ margin: '12px 0 6px', padding: '8px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.78rem', color: '#64748b', lineHeight: 1.6 }}>
        <strong style={{ color: '#334155' }}>Recommended size:</strong><br />
        Width: Full page width (1440px+) · Height: {height}px<br />
        Format: JPG or WebP · Keep file size under 300KB
      </div>

      {s.image_url && (
        <div style={{ marginBottom: 10 }}>
          <img src={s.image_url} alt="top banner preview"
            style={{ width: '100%', height: height, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0', display: 'block' }} />
        </div>
      )}

      <div className="admin-form-group">
        <label>Upload Banner Image</label>
        <input type="file" accept="image/*" onChange={upload} disabled={uploading} />
        {uploading && <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}> Uploading…</span>}
      </div>

      <div className="admin-form-group">
        <label>Or paste image URL</label>
        <input value={s.image_url || ''} onChange={e => set('image_url', e.target.value)} placeholder="https://..." />
      </div>

      <div className="admin-form-group">
        <label>Click Link <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional — where banner click goes)</span></label>
        <input value={s.link || ''} onChange={e => set('link', e.target.value)} placeholder="/products" />
      </div>

      <div className="admin-form-group">
        <label>Banner Height (px)</label>
        <input type="number" min="40" max="200" value={height}
          onChange={e => set('height', Number(e.target.value))} style={{ maxWidth: 120 }} />
      </div>

      {s.image_url && (
        <button className="btn-admin btn-admin-danger btn-admin-sm" style={{ marginTop: 8 }}
          onClick={() => set('image_url', '')}>
          Remove Image
        </button>
      )}
    </div>
  );
}

function TickerPanel({ s, set }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminApi.uploadImage(file);
      set('image_url', res.data.url);
    } catch { showAlert('Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <div className="edit-panel-section-title"><FiImage size={14} /> Announcement Banner</div>
      <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.6 }}>
        Full-width thin banner at the very top of the page.<br />
        <strong style={{ color: '#334155' }}>Recommended:</strong> 1440 × 68px · JPG or WebP
      </p>
      <Toggle label="Show Banner" checked={s.enabled !== false} onChange={v => set('enabled', v)}
        desc="Turn off to hide without deleting the image" />

      {s.image_url && (
        <div style={{ marginBottom: 12 }}>
          <img src={s.image_url} alt="Banner preview"
            style={{ width: '100%', height: 68, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)', display: 'block' }} />
        </div>
      )}

      <div className="admin-form-group">
        <label>Upload Image</label>
        <input type="file" accept="image/*" onChange={upload} disabled={uploading} />
        {uploading && <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}> Uploading…</span>}
      </div>

      {s.image_url && (
        <button className="btn-admin btn-admin-danger btn-admin-sm" onClick={() => set('image_url', '')}>
          Remove Banner
        </button>
      )}
    </div>
  );
}

function HeroPanel({ s, set }) {
  const setStat = (key, field, val) => set(key, { ...(s[key] || {}), [field]: val });
  return (
    <div>
      <div className="edit-panel-section-title"><FiLayout size={14} /> Hero Banner</div>
      <Field label="Tag Line" hint="Small label above the heading">
        <Input value={s.tag} onChange={v => set('tag', v)} placeholder="✦ New arrivals every week" />
      </Field>
      <Field label="Heading" hint="Wrap a word in <em>...</em> for italic accent">
        <textarea value={s.title || ''} onChange={e => set('title', e.target.value)} rows={2}
          style={{ width: '100%', padding: '10px 13px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '.9rem', fontFamily: 'inherit', resize: 'vertical', outline: 'none' }} />
      </Field>
      <Field label="Sub-heading">
        <textarea value={s.subtitle || ''} onChange={e => set('subtitle', e.target.value)} rows={2}
          style={{ width: '100%', padding: '10px 13px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '.9rem', fontFamily: 'inherit', resize: 'vertical', outline: 'none' }} />
      </Field>
      <div className="admin-form-row">
        <Field label="Button 1 Text"><Input value={s.btn1_text} onChange={v => set('btn1_text', v)} /></Field>
        <Field label="Button 1 Link"><Input value={s.btn1_link} onChange={v => set('btn1_link', v)} /></Field>
      </div>
      <div className="admin-form-row">
        <Field label="Button 2 Text"><Input value={s.btn2_text} onChange={v => set('btn2_text', v)} /></Field>
        <Field label="Button 2 Link"><Input value={s.btn2_link} onChange={v => set('btn2_link', v)} /></Field>
      </div>
      <div className="edit-panel-sub-title">Stats</div>
      {['stat1','stat2','stat3'].map((k, i) => (
        <div key={k} className="admin-form-row" style={{ marginBottom: 8 }}>
          <Field label={`Stat ${i+1} Value`}><Input value={s[k]?.value} onChange={v => setStat(k, 'value', v)} placeholder="50k+" /></Field>
          <Field label="Label"><Input value={s[k]?.label} onChange={v => setStat(k, 'label', v)} placeholder="Products" /></Field>
        </div>
      ))}
    </div>
  );
}

function SectionPanel({ s, set, label }) {
  return (
    <div>
      <div className="edit-panel-section-title"><FiGrid size={14} /> {label}</div>
      <Toggle label={`Show ${label} Section`} checked={s.enabled} onChange={v => set('enabled', v)} />
      <Field label="Section Label" hint="Small uppercase text above the title">
        <Input value={s.label} onChange={v => set('label', v)} />
      </Field>
      <Field label="Title">
        <Input value={s.title} onChange={v => set('title', v)} />
      </Field>
      <Field label="Subtitle">
        <textarea value={s.subtitle || ''} onChange={e => set('subtitle', e.target.value)} rows={2}
          style={{ width: '100%', padding: '10px 13px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '.9rem', fontFamily: 'inherit', resize: 'vertical', outline: 'none' }} />
      </Field>
    </div>
  );
}

// ── Featured Products Panel ─────────────────────────────────────
function FeaturedProductsPanel({ s, set, refreshIframe }) {
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [toggling, setToggling] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.getStoreProducts()
      .then(r => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (product) => {
    setToggling(product.id);
    try {
      await adminApi.toggleFeatured(product.id);
      load();
      refreshIframe();
    } catch {
    } finally {
      setToggling(null);
    }
  };

  return (
    <div>
      <div className="edit-panel-section-title"><FiStar size={14} /> Featured Products</div>
      <Toggle label="Show Featured Section" checked={s.enabled} onChange={v => set('enabled', v)} desc="Displays the featured grid on the homepage" />
      <div className="admin-form-group">
        <label>Section Label</label>
        <Input value={s.label} onChange={v => set('label', v)} placeholder="Featured" />
      </div>
      <div className="admin-form-group">
        <label>Title</label>
        <Input value={s.title} onChange={v => set('title', v)} placeholder="Trending This Week" />
      </div>
      <p style={{ fontSize: '.8125rem', color: 'var(--text-muted)', margin: '16px 0 10px', lineHeight: 1.5 }}>
        Toggle which products appear in this section. Changes take effect immediately.
      </p>
      {loading ? (
        <div className="admin-loading" style={{ padding: 20 }}><div className="admin-spinner" /></div>
      ) : products.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px 0', fontSize: '.875rem' }}>
          No products yet. Go to <strong>Products</strong> to add some first.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {products.map(p => (
            <div
              key={p.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                border: `1.5px solid ${p.is_featured ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: 10, background: 'var(--bg)',
                opacity: toggling === p.id ? 0.5 : 1,
                transition: 'border-color .2s, opacity .2s',
              }}
            >
              <div style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0, overflow: 'hidden', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {p.image_url
                  ? <img src={p.image_url} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                  : <FiStar size={16} style={{ color: '#94a3b8' }} />
                }
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '.875rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Rs {p.price}</div>
              </div>
              <label className="store-toggle" style={{ flexShrink: 0, cursor: toggling === p.id ? 'wait' : 'pointer' }}>
                <input type="checkbox" checked={!!p.is_featured} onChange={() => toggle(p)} disabled={toggling === p.id} />
                <span className="store-toggle-track" />
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Staff Favorites Panel ────────────────────────────────────────
function StaffFavoritesPanel({ s, set }) {
  const [allProducts, setAllProducts] = useState([]);
  const [search, setSearch]           = useState('');
  const [loadingP, setLoadingP]       = useState(true);

  useEffect(() => {
    adminApi.getStoreProducts()
      .then(r => setAllProducts(r.data))
      .catch(() => {})
      .finally(() => setLoadingP(false));
  }, []);

  const selected = (s.products || []);

  const isSelected = (id) => selected.some(p => (typeof p === 'object' ? p.id : p) === id);

  const addProduct = (product) => {
    if (isSelected(product.id)) return;
    set('products', [...selected, { id: product.id, name: product.name, image_url: product.image_url }]);
  };

  const removeProduct = (id) => {
    set('products', selected.filter(p => (typeof p === 'object' ? p.id : p) !== id));
  };

  const filteredAll = allProducts.filter(p =>
    !isSelected(p.id) && p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="edit-panel-section-title"><FiStar size={14} /> Staff Favorites</div>
      <Toggle label="Show Staff Favorites Section" checked={s.enabled} onChange={v => set('enabled', v)} desc="Magazine-style showcase on the homepage" />
      <div className="admin-form-group">
        <label>Section Title</label>
        <Input value={s.title} onChange={v => set('title', v)} placeholder="Staff Favorites" />
      </div>
      <div className="admin-form-group">
        <label>Subtitle</label>
        <Input value={s.subtitle} onChange={v => set('subtitle', v)} placeholder="Most liked by us" />
      </div>

      {/* Selected products */}
      <p style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--text-secondary)', margin: '18px 0 8px', textTransform: 'uppercase', letterSpacing: '.05em' }}>
        Selected ({selected.length})
      </p>
      {selected.length === 0 ? (
        <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 16 }}>No products added yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {selected.map((p, i) => {
            const id = typeof p === 'object' ? p.id : p;
            const name = typeof p === 'object' ? p.name : `Product #${p}`;
            const img  = typeof p === 'object' ? p.image_url : null;
            return (
              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1.5px solid var(--primary)', borderRadius: 10, background: 'var(--bg)' }}>
                {img && <img src={img} alt={name} style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 6, background: '#f3f4f6' }} />}
                <span style={{ flex: 1, fontSize: '.85rem', fontWeight: 600, color: 'var(--text)' }}>{name}</span>
                <button onClick={() => removeProduct(id)} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: '.75rem', fontWeight: 700 }}>Remove</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Search + add */}
      <p style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--text-secondary)', margin: '8px 0 8px', textTransform: 'uppercase', letterSpacing: '.05em' }}>
        Add Products
      </p>
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search products…"
        style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '.875rem', fontFamily: 'inherit', outline: 'none', marginBottom: 8, boxSizing: 'border-box' }}
      />
      {loadingP ? (
        <div className="admin-loading" style={{ padding: 14 }}><div className="admin-spinner" /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 220, overflowY: 'auto' }}>
          {filteredAll.slice(0, 20).map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 10, background: 'var(--bg)', cursor: 'pointer' }}
              onClick={() => addProduct(p)}>
              {p.image_url && <img src={p.image_url} alt={p.name} style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 5, background: '#f3f4f6' }} />}
              <span style={{ flex: 1, fontSize: '.83rem', color: 'var(--text)' }}>{p.name}</span>
              <span style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>+ Add</span>
            </div>
          ))}
          {filteredAll.length === 0 && (
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '.82rem', padding: '10px 0' }}>No products found</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Category Search Picker (shared) ──────────────────────────────
function CategorySearchPicker({ available, onSelect }) {
  const [query, setQuery] = useState('');
  const filtered = available.filter(c => c.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div style={{ border: '1.5px solid var(--border)', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderBottom: '1px solid var(--border)', background: '#f8fafc' }}>
        <FiSearch size={13} color="var(--text-muted)" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search categories…"
          style={{ border: 'none', outline: 'none', flex: 1, fontSize: '.85rem', fontFamily: 'inherit', background: 'transparent' }}
        />
        {query && (
          <button onClick={() => setQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--text-muted)' }}>
            <FiX size={13} />
          </button>
        )}
      </div>
      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
        {available.length === 0 ? (
          <div style={{ padding: '12px', fontSize: '.8rem', color: 'var(--text-muted)', textAlign: 'center' }}>All categories already added</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '12px', fontSize: '.8rem', color: 'var(--text-muted)', textAlign: 'center' }}>No categories found</div>
        ) : filtered.map(c => (
          <div
            key={c.id}
            onClick={() => { onSelect(c.id); setQuery(''); }}
            style={{ padding: '8px 12px', fontSize: '.875rem', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}
            onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <FiPlus size={12} color="var(--text-muted)" />
            {c.name}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Popular Categories Panel ─────────────────────────────────────
function PopularCategoriesPanel({ settings, setSettings }) {
  const [allCategories, setAllCategories] = useState([]);
  const [uploading, setUploading]         = useState(null);
  const [overIdx, setOverIdx]             = useState(null);
  const dragIdx                           = useRef(null);
  const items = settings.popularCategories || [];

  useEffect(() => {
    adminApi.getCategories().then(r => setAllCategories(r.data)).catch(() => {});
  }, []);

  const setItems = (newItems) => setSettings(prev => ({ ...prev, popularCategories: newItems }));

  const onDragStart = (idx) => { dragIdx.current = idx; };
  const onDragOver  = (e, idx) => { e.preventDefault(); setOverIdx(idx); };
  const onDrop      = (idx) => {
    if (dragIdx.current === null || dragIdx.current === idx) { setOverIdx(null); return; }
    const a = [...items];
    const [moved] = a.splice(dragIdx.current, 1);
    a.splice(idx, 0, moved);
    setItems(a);
    dragIdx.current = null;
    setOverIdx(null);
  };

  const addCategory = (catId) => {
    if (!catId || items.find(i => i.category_id === Number(catId))) return;
    const cat = allCategories.find(c => c.id === Number(catId));
    setItems([...items, { category_id: Number(catId), name: cat?.name || '', image_url: cat?.image_url || '', link: '' }]);
  };

  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));

  const uploadImg = async (e, idx) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(idx);
    try {
      const res = await adminApi.uploadImage(file);
      setItems(items.map((it, i) => i === idx ? { ...it, image_url: res.data.url } : it));
    } catch { showAlert('Upload failed'); }
    finally { setUploading(null); }
  };

  const available = allCategories.filter(c => !items.find(i => i.category_id === c.id));

  const addSpecial = (type) => {
    const map = { new_arrivals: ['New Arrivals', '/new-arrivals'], day_sale: ['Day Sale', '/deals'], most_selling: ['Most Selling', '/most-selling'] };
    const [label, link] = map[type] || [];
    if (!link || items.find(i => i.link === link)) return;
    setItems([...items, { category_id: null, name: label, image_url: '', link }]);
  };

  return (
    <div>
      <div className="edit-panel-section-title"><FiGrid size={14} /> Popular Categories</div>
      <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.6 }}>
        Category cards shown below the homepage banner.<br />
        <strong style={{ color: '#334155' }}>Image size: 400 × 400 px</strong> · Square 1:1 · JPG or WebP<br />
        <span style={{ color: '#94a3b8' }}>Keep file size under 200KB for fast loading.</span>
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button type="button" className="btn-admin btn-admin-ghost btn-admin-sm"
          onClick={() => addSpecial('new_arrivals')}
          disabled={!!items.find(i => i.link === '/new-arrivals')}
          title="New Arrivals card add karo"
        >
          ⭐ + New Arrivals
        </button>
        <button type="button" className="btn-admin btn-admin-ghost btn-admin-sm"
          onClick={() => addSpecial('day_sale')}
          disabled={!!items.find(i => i.link === '/deals')}
          title="Day Sale card add karo"
        >
          ⚡ + Day Sale
        </button>
        <button type="button" className="btn-admin btn-admin-ghost btn-admin-sm"
          onClick={() => addSpecial('most_selling')}
          disabled={!!items.find(i => i.link === '/most-selling')}
          title="Most Selling card add karo"
        >
          🔥 + Most Selling
        </button>
      </div>

      {items.map((item, idx) => (
        <div
          key={idx}
          draggable
          onDragStart={() => onDragStart(idx)}
          onDragOver={e => onDragOver(e, idx)}
          onDrop={() => onDrop(idx)}
          onDragEnd={() => setOverIdx(null)}
          style={{
            padding: '10px 12px',
            background: overIdx === idx ? '#eff6ff' : '#f8fafc',
            border: `1.5px solid ${overIdx === idx ? '#3b82f6' : '#e2e8f0'}`,
            borderRadius: 6, marginBottom: 8, cursor: 'grab',
            transition: 'border-color .15s, background .15s',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ color: '#94a3b8', fontSize: '1.1rem', cursor: 'grab', userSelect: 'none' }}>⠿</span>
            <span style={{ fontSize: '.72rem', fontWeight: 700, color: 'var(--text-muted)', minWidth: 18, textAlign: 'center' }}>{idx + 1}</span>
            {item.image_url && (
              <img src={item.image_url} alt={item.name}
                style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', border: '1px solid #e2e8f0' }} />
            )}
            <span style={{ fontWeight: 700, flex: 1, fontSize: '.875rem', textTransform: 'uppercase' }}>{item.name}</span>
            <button className="btn-admin btn-admin-danger btn-admin-sm" onClick={() => remove(idx)}>
              <FiTrash2 size={12} />
            </button>
          </div>
          <div className="admin-form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: '.75rem' }}>Category Image <span style={{ color: '#94a3b8' }}>(400×400px recommended)</span></label>
            <input type="file" accept="image/*" onChange={e => uploadImg(e, idx)} disabled={uploading === idx} />
            {uploading === idx && <span style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}> Uploading…</span>}
          </div>
        </div>
      ))}

      {available.length > 0 && (
        <div className="admin-form-group">
          <label>Add Category</label>
          <CategorySearchPicker available={available} onSelect={addCategory} />
        </div>
      )}
    </div>
  );
}

// ── Category Strip Panel ─────────────────────────────────────────
function CategoryStripPanel({ settings, setSettings }) {
  const [allCategories, setAllCategories] = useState([]);
  const [uploading, setUploading]         = useState(null);
  const items = settings.categoryStrip || [];

  useEffect(() => {
    adminApi.getCategories().then(r => setAllCategories(r.data)).catch(() => {});
  }, []);

  const setItems = (newItems) => setSettings(prev => ({ ...prev, categoryStrip: newItems }));

  const addCategory = (catId) => {
    if (!catId || items.find(i => i.category_id === Number(catId))) return;
    const cat = allCategories.find(c => c.id === Number(catId));
    setItems([...items, { category_id: Number(catId), name: cat?.name || '', image_url: cat?.image_url || '', link: '' }]);
  };

  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));

  const uploadImg = async (e, idx) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(idx);
    try {
      const res = await adminApi.uploadImage(file);
      setItems(items.map((it, i) => i === idx ? { ...it, image_url: res.data.url } : it));
    } catch { showAlert('Upload failed'); }
    finally { setUploading(null); }
  };

  const available = allCategories.filter(c => !items.find(i => i.category_id === c.id));

  return (
    <div>
      <div className="edit-panel-section-title"><FiLayout size={14} /> Category Strip</div>
      <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
        Clickable category icons shown below the homepage banner. Each has a circular image and name.
      </p>

      {items.map((item, idx) => (
        <div key={idx} style={{ padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            {item.image_url && (
              <img src={item.image_url} alt={item.name}
                style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
            )}
            <span style={{ fontWeight: 600, flex: 1, fontSize: '.875rem' }}>{item.name}</span>
            <button className="btn-admin btn-admin-danger btn-admin-sm" onClick={() => remove(idx)}>
              <FiTrash2 size={12} />
            </button>
          </div>
          <div className="admin-form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: '.75rem' }}>Image</label>
            <input type="file" accept="image/*" onChange={e => uploadImg(e, idx)} disabled={uploading === idx} />
            {uploading === idx && <span style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}> Uploading…</span>}
          </div>
        </div>
      ))}

      {available.length > 0 && (
        <div className="admin-form-group">
          <label>Add Category</label>
          <CategorySearchPicker available={available} onSelect={addCategory} />
        </div>
      )}
    </div>
  );
}

// ── Homepage Categories Panel ────────────────────────────────────
function DragList({ items, keyFor, labelFor, onReorder, onRemove }) {
  const dragIdx = useRef(null);
  const [overIdx, setOverIdx] = useState(null);

  const onDragStart = (idx) => { dragIdx.current = idx; };
  const onDragOver  = (e, idx) => { e.preventDefault(); setOverIdx(idx); };
  const onDrop      = (idx) => {
    if (dragIdx.current === null || dragIdx.current === idx) { setOverIdx(null); return; }
    const a = [...items];
    const [moved] = a.splice(dragIdx.current, 1);
    a.splice(idx, 0, moved);
    onReorder(a);
    dragIdx.current = null;
    setOverIdx(null);
  };

  const move = (idx, dir) => {
    const a = [...items];
    const j = idx + dir;
    if (j < 0 || j >= a.length) return;
    [a[idx], a[j]] = [a[j], a[idx]];
    onReorder(a);
  };

  return (
    <div style={{ marginBottom: 14 }}>
      {items.map((sec, idx) => (
        <div
          key={keyFor(sec)}
          draggable
          onDragStart={() => onDragStart(idx)}
          onDragOver={e => onDragOver(e, idx)}
          onDrop={() => onDrop(idx)}
          onDragEnd={() => setOverIdx(null)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', background: overIdx === idx ? '#eff6ff' : '#f8fafc',
            border: `1.5px solid ${overIdx === idx ? '#3b82f6' : '#e2e8f0'}`,
            borderRadius: 6, marginBottom: 6, cursor: 'grab',
            transition: 'border-color .15s, background .15s',
          }}
        >
          <span style={{ color: '#94a3b8', fontSize: '1rem', cursor: 'grab', userSelect: 'none' }}>⠿</span>
          <span style={{ fontSize: '.72rem', fontWeight: 700, color: 'var(--text-muted)', minWidth: 18, textAlign: 'center' }}>{idx + 1}</span>
          <span style={{ fontSize: '.875rem', fontWeight: 600, flex: 1 }}>{labelFor(sec)}</span>
          <button className="banner-icon-btn" onClick={() => move(idx, -1)} disabled={idx === 0} title="Move up"><FiChevronUp size={13} /></button>
          <button className="banner-icon-btn" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} title="Move down"><FiChevronDown size={13} /></button>
          <button className="btn-admin btn-admin-danger btn-admin-sm" onClick={() => onRemove(keyFor(sec))}>
            <FiTrash2 size={12} /> Remove
          </button>
        </div>
      ))}
    </div>
  );
}


function HomepageCategoriesPanel({ settings, setSettings }) {
  const [allCategories, setAllCategories] = useState([]);
  const selected = settings.homepageCategories || [];

  useEffect(() => {
    adminApi.getCategories().then(r => setAllCategories(r.data)).catch(() => {});
  }, []);

  const addCategory = (catId) => {
    if (!catId || selected.find(s => s.category_id === Number(catId))) return;
    setSettings(prev => ({ ...prev, homepageCategories: [...selected, { category_id: Number(catId) }] }));
  };

  const removeByKey = (key) => {
    setSettings(prev => ({
      ...prev,
      homepageCategories: (prev.homepageCategories || []).filter(s =>
        key.startsWith('type:') ? s.type !== key.replace('type:', '') : s.category_id !== key
      )
    }));
  };

  const addSpecial = (type) => {
    if (selected.find(s => s.type === type)) return;
    setSettings(prev => ({ ...prev, homepageCategories: [...selected, { type }] }));
  };

  const catName = (id) => allCategories.find(c => c.id === id)?.name || `Category ${id}`;
  const available = allCategories.filter(c => !selected.find(s => s.category_id === c.id));

  const labelFor = (sec) => {
    if (sec.type === 'new_arrivals')  return '⭐ New Arrivals';
    if (sec.type === 'day_sale')      return '⚡ Day Sale';
    if (sec.type === 'most_selling')  return '🔥 Most Selling';
    return catName(sec.category_id);
  };

  const keyFor = (sec) => sec.type ? `type:${sec.type}` : sec.category_id;

  return (
    <div>
      <div className="edit-panel-section-title"><FiGrid size={14} /> Homepage Category Sections</div>
      <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
        Select categories to show as product sections on the homepage.
      </p>

      {/* Special section buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <button type="button" className="btn-admin btn-admin-ghost btn-admin-sm"
          onClick={() => addSpecial('new_arrivals')}
          disabled={!!selected.find(s => s.type === 'new_arrivals')}
        >⭐ + New Arrivals</button>
        <button type="button" className="btn-admin btn-admin-ghost btn-admin-sm"
          onClick={() => addSpecial('day_sale')}
          disabled={!!selected.find(s => s.type === 'day_sale')}
        >⚡ + Day Sale</button>
        <button type="button" className="btn-admin btn-admin-ghost btn-admin-sm"
          onClick={() => addSpecial('most_selling')}
          disabled={!!selected.find(s => s.type === 'most_selling')}
        >🔥 + Most Selling</button>
      </div>

      {selected.length > 0 && (
        <DragList
          items={selected}
          keyFor={keyFor}
          labelFor={labelFor}
          onReorder={newList => setSettings(prev => ({ ...prev, homepageCategories: newList }))}
          onRemove={removeByKey}
        />
      )}

      {available.length > 0 && (
        <div className="admin-form-group">
          <label>Add Category</label>
          <CategorySearchPicker available={available} onSelect={addCategory} />
        </div>
      )}
    </div>
  );
}

// ── New Arrivals Panel ───────────────────────────────────────────
function NewArrivalsPanel({ s, set }) {
  const [allProducts, setAllProducts] = useState([]);
  const [search, setSearch]           = useState('');
  const [loadingP, setLoadingP]       = useState(true);

  useEffect(() => {
    adminApi.getStoreProducts()
      .then(r => setAllProducts(r.data))
      .catch(() => {})
      .finally(() => setLoadingP(false));
  }, []);

  const selected = s.products || [];
  const isSelected = (id) => selected.some(p => (typeof p === 'object' ? p.id : p) === id);

  const addProduct = (product) => {
    if (isSelected(product.id)) return;
    set('products', [...selected, { id: product.id, name: product.name, image_url: product.image_url }]);
  };

  const removeProduct = (id) => {
    set('products', selected.filter(p => (typeof p === 'object' ? p.id : p) !== id));
  };

  const filteredAll = allProducts.filter(p =>
    !isSelected(p.id) && p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="edit-panel-section-title"><FiStar size={14} /> New Arrivals</div>
      <Toggle label="Show New Arrivals Section" checked={s.enabled} onChange={v => set('enabled', v)} desc="Small product grid at the bottom of the homepage" />
      <div className="admin-form-group">
        <label>Section Title</label>
        <Input value={s.title} onChange={v => set('title', v)} placeholder="New Arrivals" />
      </div>
      <div className="admin-form-group">
        <label>Subtitle</label>
        <Input value={s.subtitle} onChange={v => set('subtitle', v)} placeholder="Discover something New" />
      </div>

      <p style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--text-secondary)', margin: '18px 0 8px', textTransform: 'uppercase', letterSpacing: '.05em' }}>
        Selected ({selected.length})
      </p>
      {selected.length === 0 ? (
        <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 16 }}>No products added yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {selected.map(p => {
            const id   = typeof p === 'object' ? p.id : p;
            const name = typeof p === 'object' ? p.name : `Product #${p}`;
            const img  = typeof p === 'object' ? p.image_url : null;
            return (
              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1.5px solid var(--primary)', borderRadius: 10, background: 'var(--bg)' }}>
                {img && <img src={img} alt={name} style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 6, background: '#f3f4f6' }} />}
                <span style={{ flex: 1, fontSize: '.85rem', fontWeight: 600, color: 'var(--text)' }}>{name}</span>
                <button onClick={() => removeProduct(id)} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: '.75rem', fontWeight: 700 }}>Remove</button>
              </div>
            );
          })}
        </div>
      )}

      <p style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--text-secondary)', margin: '8px 0 8px', textTransform: 'uppercase', letterSpacing: '.05em' }}>
        Add Products
      </p>
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search products…"
        style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '.875rem', fontFamily: 'inherit', outline: 'none', marginBottom: 8, boxSizing: 'border-box' }}
      />
      {loadingP ? (
        <div className="admin-loading" style={{ padding: 14 }}><div className="admin-spinner" /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 220, overflowY: 'auto' }}>
          {filteredAll.slice(0, 20).map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 10, background: 'var(--bg)', cursor: 'pointer' }}
              onClick={() => addProduct(p)}>
              {p.image_url && <img src={p.image_url} alt={p.name} style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 5, background: '#f3f4f6' }} />}
              <span style={{ flex: 1, fontSize: '.83rem', color: 'var(--text)' }}>{p.name}</span>
              <span style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>+ Add</span>
            </div>
          ))}
          {filteredAll.length === 0 && (
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '.82rem', padding: '10px 0' }}>No products found</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Category Carousel Panel ─────────────────────────────────────
const BG_PRESETS = [
  'linear-gradient(135deg,#ef4444,#dc2626)',
  'linear-gradient(135deg,#f97316,#ea580c)',
  'linear-gradient(135deg,#22c55e,#16a34a)',
  'linear-gradient(135deg,#8b5cf6,#7c3aed)',
  'linear-gradient(135deg,#ec4899,#db2777)',
  'linear-gradient(135deg,#0ea5e9,#0284c7)',
  'linear-gradient(135deg,#f59e0b,#d97706)',
  'linear-gradient(135deg,#14b8a6,#0d9488)',
];

function CarouselCatsPanel({ cats, setCats }) {
  const [dbCats,    setDbCats]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    adminApi.getStoreCategories()
      .then(r => setDbCats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Find existing config for a DB category (matched by name)
  const getConfig = (dbCat) => cats.find(c => c.name === dbCat.name);

  const toggle = (dbCat) => {
    if (getConfig(dbCat)) {
      setCats(cats.filter(c => c.name !== dbCat.name));
      if (expandedId === dbCat.id) setExpandedId(null);
    } else {
      const idx = cats.length % BG_PRESETS.length;
      setCats([...cats, {
        name: dbCat.name,
        id: dbCat.id,
        image_url: dbCat.image_url || '',
        emoji: '📦',
        bg: BG_PRESETS[idx],
        link: `/products?category_id=${dbCat.id}`,
      }]);
    }
  };

  const update = (name, field, val) => {
    setCats(cats.map(c => c.name === name ? { ...c, [field]: val } : c));
  };

  const move = (name, dir) => {
    const i = cats.findIndex(c => c.name === name);
    const arr = [...cats];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setCats(arr);
  };

  return (
    <div>
      <div className="edit-panel-section-title"><FiGrid size={14} /> Shop by Category</div>
      <p style={{ fontSize: '.8125rem', color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
        Toggle categories on to show them in the carousel. Click the edit icon to upload an image for each one.
      </p>

      {loading ? (
        <div className="admin-loading" style={{ padding: 20 }}><div className="admin-spinner" /></div>
      ) : dbCats.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 0', fontSize: '.875rem' }}>
          No categories found. Go to <strong>Categories</strong> to create some first.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {dbCats.map((dbCat) => {
            const config    = getConfig(dbCat);
            const enabled   = !!config;
            const expanded  = expandedId === dbCat.id;
            const thumb     = config?.image_url || dbCat.image_url || '';

            return (
              <div
                key={dbCat.id}
                style={{
                  border: `1.5px solid ${enabled ? 'var(--primary)' : 'var(--border)'}`,
                  borderRadius: 10,
                  overflow: 'hidden',
                  background: 'var(--bg)',
                  opacity: enabled ? 1 : 0.7,
                  transition: 'border-color .2s, opacity .2s',
                }}
              >
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
                  {/* Thumb */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, flexShrink: 0, overflow: 'hidden',
                    background: config?.bg || '#e5e7eb',
                  }}>
                    {thumb
                      ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                      : <span style={{ fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>{config?.emoji || '📦'}</span>
                    }
                  </div>

                  {/* Name */}
                  <span style={{ flex: 1, fontWeight: 600, fontSize: '.875rem', color: 'var(--text)' }}>{dbCat.name}</span>

                  {/* Reorder arrows — only when enabled */}
                  {enabled && (() => {
                    const i = cats.findIndex(c => c.name === dbCat.name);
                    return (
                      <>
                        <button className="banner-icon-btn" onClick={() => move(dbCat.name, -1)} disabled={i === 0} title="Move up"><FiChevronUp size={13} /></button>
                        <button className="banner-icon-btn" onClick={() => move(dbCat.name, 1)} disabled={i === cats.length - 1} title="Move down"><FiChevronDown size={13} /></button>
                        <button
                          className="banner-icon-btn"
                          onClick={() => setExpandedId(expanded ? null : dbCat.id)}
                          style={{ color: expanded ? 'var(--primary)' : undefined }}
                          title="Edit image & settings"
                        >
                          <FiEdit2 size={13} />
                        </button>
                      </>
                    );
                  })()}

                  {/* Toggle */}
                  <label className="store-toggle" style={{ flexShrink: 0, cursor: 'pointer' }}>
                    <input type="checkbox" checked={enabled} onChange={() => toggle(dbCat)} />
                    <span className="store-toggle-track" />
                  </label>
                </div>

                {/* Expanded settings */}
                {enabled && expanded && (
                  <div style={{ padding: '0 12px 14px', borderTop: '1px solid var(--border)' }}>
                    <ImageUpload
                      label="Carousel Image"
                      value={config.image_url}
                      onChange={v => update(dbCat.name, 'image_url', v)}
                      height={90}
                      uploadOnly
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Carousel order summary */}
      {cats.length > 1 && (
        <div style={{ marginTop: 20, padding: '12px 14px', background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 700, fontSize: '.8rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 8 }}>
            Carousel order ({cats.length} shown)
          </div>
          {cats.map((cat, i) => (
            <div key={cat.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: '.85rem' }}>
              <span style={{ color: 'var(--text-muted)', fontWeight: 700, width: 18 }}>{i + 1}.</span>
              <span style={{ flex: 1, color: 'var(--text)' }}>{cat.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Featured Category Cards Panel ───────────────────────────────
const EMPTY_CAT = { name: '', image_url: '', link: '/products' };

function FeaturedCatsPanel({ cats, setCats }) {
  const [editing, setEditing] = useState(null); // index or null
  const [form,    setForm]    = useState(EMPTY_CAT);
  const [saving,  setSaving]  = useState(false);

  const openAdd  = () => { setForm({ ...EMPTY_CAT }); setEditing('new'); };
  const openEdit = (i) => { setForm({ ...cats[i] }); setEditing(i); };
  const cancel   = () => setEditing(null);

  const save = () => {
    if (!form.name.trim()) return;
    setSaving(true);
    if (editing === 'new') {
      setCats([...cats, form]);
    } else {
      const arr = [...cats];
      arr[editing] = form;
      setCats(arr);
    }
    setEditing(null);
    setSaving(false);
  };

  const remove = async (i) => {
    if (!await showDelete('Remove this category card?', 'Remove')) return;
    setCats(cats.filter((_, idx) => idx !== i));
  };

  const move = (i, dir) => {
    const arr = [...cats];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setCats(arr);
  };

  if (editing !== null) {
    return (
      <div>
        <button className="btn-admin btn-admin-ghost btn-admin-sm" onClick={cancel} style={{ marginBottom: 14, gap: 6 }}>
          <FiArrowLeft size={12} /> Back
        </button>
        <div className="edit-panel-section-title">
          <FiLayout size={14} />{editing === 'new' ? 'Add Category Card' : 'Edit Category Card'}
        </div>
        <ImageUpload
          label="Category Image"
          value={form.image_url}
          onChange={v => setForm(f => ({ ...f, image_url: v }))}
          height={120}
        />
        <Field label="Name *">
          <Input value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="Category name" />
        </Field>
        <Field label="Link URL">
          <Input value={form.link} onChange={v => setForm(f => ({ ...f, link: v }))} placeholder="/products" />
        </Field>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn-admin btn-admin-ghost" onClick={cancel} style={{ flex: 1 }}>Cancel</button>
          <button className="btn-admin btn-admin-primary" onClick={save} disabled={saving || !form.name.trim()} style={{ flex: 1 }}>
            {editing === 'new' ? 'Add Card' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="edit-panel-section-title"><FiLayout size={14} /> Category Cards</div>
      <p style={{ fontSize: '.8125rem', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
        Featured category cards shown below the banner. Each card has an image and a name.
      </p>
      <button
        className="btn-admin btn-admin-primary"
        style={{ width: '100%', justifyContent: 'center', marginBottom: 16 }}
        onClick={openAdd}
      >
        <FiPlus size={14} /> Add Category Card
      </button>

      {cats.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px 0', fontSize: '.875rem' }}>
          No cards yet. Add your first category card!
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {cats.map((cat, i) => (
            <div key={i} className="banner-list-item">
              <div className="banner-list-thumb" style={{ backgroundColor: '#f1f5f9' }}>
                {cat.image_url
                  ? <img src={cat.image_url} alt={cat.name} onError={e => { e.target.style.display = 'none'; }} />
                  : <FiImage size={20} style={{ color: '#94a3b8' }} />
                }
                <span className="banner-list-num">{i + 1}</span>
              </div>
              <div className="banner-list-info">
                <div className="banner-list-title">{cat.name || <em style={{ color: 'var(--text-muted)' }}>Untitled</em>}</div>
                <div className="banner-list-url">{cat.link || '/products'}</div>
              </div>
              <div className="banner-list-actions">
                <button className="banner-icon-btn" onClick={() => move(i, -1)} disabled={i === 0} title="Move up"><FiChevronUp size={13} /></button>
                <button className="banner-icon-btn" onClick={() => move(i, 1)} disabled={i === cats.length - 1} title="Move down"><FiChevronDown size={13} /></button>
                <button className="banner-icon-btn" onClick={() => openEdit(i)} title="Edit"><FiEdit2 size={13} /></button>
                <button className="banner-icon-btn danger" onClick={() => remove(i)} title="Delete"><FiTrash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Navigation Panel ────────────────────────────────────────────
const PRESET_PAGES = [
  { label: 'Home',        to: '/' },
  { label: 'All Products', to: '/products' },
  { label: 'Cart',        to: '/cart' },
  { label: 'Track Order', to: '/orders' },
  { label: 'Login',       to: '/login' },
  { label: 'Profile',     to: '/profile' },
];

function NavPanel({ links, setLinks }) {
  const [dbCats, setDbCats]         = useState([]);
  const [showPagePicker, setShowPagePicker]   = useState(false);
  const [showCatPicker,  setShowCatPicker]    = useState(false);

  useEffect(() => {
    adminApi.getStoreCategories().then(r => setDbCats(r.data)).catch(() => {});
  }, []);

  const update = (i, field, val) => {
    const arr = [...links];
    arr[i] = { ...arr[i], [field]: val };
    setLinks(arr);
  };

  const move = (i, dir) => {
    const arr = [...links];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setLinks(arr);
  };

  const remove = async (i) => {
    if (!await showDelete('Remove this link?', 'Remove')) return;
    setLinks(links.filter((_, idx) => idx !== i));
  };

  const addPage = (page) => {
    setLinks([...links, { label: page.label, to: page.to }]);
    setShowPagePicker(false);
  };

  const addCategory = (cat) => {
    setLinks([...links, { label: cat.name, to: `/products?cat=${cat.id}` }]);
    setShowCatPicker(false);
  };

  const pickerStyle = {
    border: '1.5px solid var(--border)', borderRadius: 10,
    background: 'var(--bg)', marginBottom: 14, overflow: 'hidden',
  };
  const pickerHeaderStyle = {
    padding: '8px 12px', fontSize: '.75rem', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '.06em',
    color: 'var(--text-muted)', borderBottom: '1px solid var(--border)',
  };
  const pickerBtnStyle = {
    display: 'block', width: '100%', textAlign: 'left',
    padding: '9px 14px', fontSize: '.875rem', fontWeight: 500,
    color: 'var(--text)', background: 'none', border: 'none',
    borderBottom: '1px solid var(--border)', cursor: 'pointer',
    fontFamily: 'inherit',
  };
  const inputStyle = {
    flex: 1, minWidth: 0, padding: '8px 10px', border: '1.5px solid var(--border)',
    borderRadius: 8, fontSize: '.85rem', fontFamily: 'inherit',
    outline: 'none', background: 'var(--surface)', color: 'var(--text)',
  };

  return (
    <div>
      <div className="edit-panel-section-title"><FiLink size={14} /> Navigation Links</div>
      <p style={{ fontSize: '.8125rem', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
        Add, remove or reorder the links shown in the header menu. Hit <strong>Save Changes</strong> to publish.
      </p>

      {/* Add buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          className="btn-admin btn-admin-primary"
          style={{ flex: 1, justifyContent: 'center' }}
          onClick={() => { setShowPagePicker(v => !v); setShowCatPicker(false); }}
        >
          <FiPlus size={13} /> Add Page
        </button>
        <button
          className="btn-admin btn-admin-ghost"
          style={{ flex: 1, justifyContent: 'center' }}
          onClick={() => { setShowCatPicker(v => !v); setShowPagePicker(false); }}
        >
          <FiGrid size={13} /> Add Category
        </button>
      </div>

      {/* Page picker dropdown */}
      {showPagePicker && (
        <div style={pickerStyle}>
          <div style={pickerHeaderStyle}>Select a page</div>
          {PRESET_PAGES.map(page => (
            <button key={page.to} onClick={() => addPage(page)} style={pickerBtnStyle}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <span style={{ fontWeight: 600 }}>{page.label}</span>
              <span style={{ marginLeft: 8, fontSize: '.78rem', color: 'var(--text-muted)' }}>{page.to}</span>
            </button>
          ))}
          <button onClick={() => addPage({ label: 'Custom Page', to: '/' })} style={{ ...pickerBtnStyle, borderBottom: 'none', color: 'var(--text-muted)', fontStyle: 'italic' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            + Custom link…
          </button>
        </div>
      )}

      {/* Category picker dropdown */}
      {showCatPicker && (
        <div style={pickerStyle}>
          <div style={pickerHeaderStyle}>Pick a category</div>
          {dbCats.length === 0 ? (
            <div style={{ padding: '12px', fontSize: '.85rem', color: 'var(--text-muted)' }}>No categories found.</div>
          ) : dbCats.map(cat => (
            <button key={cat.id} onClick={() => addCategory(cat)} style={pickerBtnStyle}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Link list */}
      {links.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px 0', fontSize: '.875rem' }}>
          No links yet. Add a page or category above.
        </div>
      ) : links.map((link, i) => (
        <div key={i} style={{
          border: '1.5px solid var(--border)', borderRadius: 10,
          padding: '10px 12px', marginBottom: 8, background: 'var(--bg)', overflow: 'hidden',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
            <span style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: '.875rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {link.label || 'Untitled'}
              {link.to?.startsWith('/products?cat=') && (
                <span style={{ marginLeft: 6, fontSize: '.68rem', fontWeight: 700, background: 'var(--primary-light)', color: 'var(--primary)', padding: '1px 7px', borderRadius: 100 }}>
                  CATEGORY
                </span>
              )}
            </span>
            <button className="banner-icon-btn" onClick={() => move(i, -1)} disabled={i === 0} title="Move up"><FiChevronUp size={13} /></button>
            <button className="banner-icon-btn" onClick={() => move(i, 1)} disabled={i === links.length - 1} title="Move down"><FiChevronDown size={13} /></button>
            <button className="banner-icon-btn danger" onClick={() => remove(i)} title="Delete"><FiTrash2 size={13} /></button>
          </div>
          <div style={{ display: 'flex', gap: 6, width: '100%' }}>
            <input value={link.label} onChange={e => update(i, 'label', e.target.value)} placeholder="Label" style={inputStyle} />
            <input value={link.to} onChange={e => update(i, 'to', e.target.value)} placeholder="/url" style={{ ...inputStyle, flex: '0 0 90px' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────
const DESKTOP_W = 1280;
const MOBILE_W  = 390;

// ── Mid Banner Panel ────────────────────────────────────────────
function MidBannerPanel({ s, set, settings, setSettings }) {
  const cats = settings.homepageCategories || [];
  const pos  = cats.findIndex(c => c.type === 'mid_banner');
  const inPage = pos !== -1;

  const addToPage = () => {
    setSettings(prev => ({ ...prev, homepageCategories: [...(prev.homepageCategories || []), { type: 'mid_banner' }] }));
  };
  const removeFromPage = () => {
    setSettings(prev => ({ ...prev, homepageCategories: (prev.homepageCategories || []).filter(c => c.type !== 'mid_banner') }));
  };
  const move = (dir) => {
    const arr = [...cats];
    const j = pos + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[pos], arr[j]] = [arr[j], arr[pos]];
    setSettings(prev => ({ ...prev, homepageCategories: arr }));
  };

  return (
    <div>
      <div className="edit-panel-section-title"><FiImage size={14} /> Mid-Page Banner</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '8px 12px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 8 }}>
        <FiImage size={13} style={{ color: '#f59e0b', flexShrink: 0 }} />
        <span style={{ fontSize: '.78rem', color: '#92400e', fontWeight: 600 }}>
          Recommended size: <strong>1250 × 500 px</strong> &nbsp;·&nbsp; JPG or WebP
        </span>
      </div>

      <ImageUpload
        label="Banner Image"
        value={s.image_url}
        onChange={v => set('image_url', v)}
        hint="Recommended: 1250 × 500 px · JPG or WebP for best quality"
        type="banner"
      />

      {s.image_url && (
        <button className="btn-admin btn-admin-danger btn-admin-sm" style={{ marginBottom: 16 }}
          onClick={() => set('image_url', '')}>
          <FiTrash2 size={12} /> Delete Image
        </button>
      )}

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
        <div style={{ fontWeight: 700, fontSize: '.8rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 10 }}>
          Page Position
        </div>

        {!inPage ? (
          <button
            className="btn-admin btn-admin-primary"
            style={{ width: '100%', justifyContent: 'center' }}
            onClick={addToPage}
            disabled={!s.image_url}
          >
            <FiPlus size={13} /> Add to Homepage
          </button>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 8, marginBottom: 10 }}>
              <span style={{ flex: 1, fontSize: '.875rem', color: '#166534', fontWeight: 600 }}>
                Showing on homepage (position {pos + 1})
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-admin btn-admin-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => move(-1)} disabled={pos === 0}>
                <FiChevronUp size={14} /> Move Up
              </button>
              <button className="btn-admin btn-admin-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => move(1)} disabled={pos === cats.length - 1}>
                <FiChevronDown size={14} /> Move Down
              </button>
            </div>
            <button className="btn-admin btn-admin-danger btn-admin-sm" style={{ marginTop: 10, width: '100%', justifyContent: 'center' }} onClick={removeFromPage}>
              <FiTrash2 size={12} /> Remove from Homepage
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminStore() {
  const iframeRef   = useRef(null);
  const previewRef  = useRef(null);
  const [settings, setSettings]   = useState(DEFAULT_SETTINGS);
  const [selected, setSelected]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [dirty, setDirty]         = useState(false);
  const [viewport, setViewport]   = useState('desktop');
  const [loading, setLoading]     = useState(true);
  const [previewScale, setPreviewScale]   = useState(1);
  const [previewHeight, setPreviewHeight] = useState(900);

  // Load current settings from API
  useEffect(() => {
    adminApi.getStoreSettings()
      .then(r => { if (r.data?.settings) setSettings(r.data.settings); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Send live preview to iframe whenever settings change
  const pushPreview = useCallback((s) => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'PREVIEW_UPDATE', settings: s }, '*');
  }, []);

  useEffect(() => {
    if (!loading) pushPreview(settings);
  }, [settings, loading, pushPreview]);

  // Listen for clicks inside the iframe (section clicked)
  useEffect(() => {
    const handle = (e) => {
      if (e.data?.type === 'SECTION_CLICKED') {
        setSelected(e.data.section);
      }
    };
    window.addEventListener('message', handle);
    return () => window.removeEventListener('message', handle);
  }, []);

  // Scale iframe to fill preview area at full desktop width
  useEffect(() => {
    const targetW = viewport === 'mobile' ? MOBILE_W : DESKTOP_W;
    const obs = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      const scale = width / targetW;
      setPreviewScale(scale);
      setPreviewHeight(Math.round(height / scale));
    });
    if (previewRef.current) obs.observe(previewRef.current);
    return () => obs.disconnect();
  }, [viewport, selected]); // recalculate when panel opens/closes too

  // Deep set a section key
  const updateSection = (section, key, value) => {
    setSettings(prev => ({ ...prev, [section]: { ...(prev[section] || {}), [key]: value } }));
    setDirty(true);
  };

  const setSectionKey = (section) => (key, value) => updateSection(section, key, value);

  // Replace entire navLinks array
  const setNavLinks = (links) => {
    setSettings(prev => ({ ...prev, navLinks: links }));
    setDirty(true);
  };

  // Replace entire featuredCats array
  const setFeaturedCats = (cats) => {
    setSettings(prev => ({ ...prev, featuredCats: cats }));
    setDirty(true);
  };

  // Replace entire carouselCats array
  const setCarouselCats = (cats) => {
    setSettings(prev => ({ ...prev, carouselCats: cats }));
    setDirty(true);
  };

  // Save to backend
  const save = async () => {
    setSaving(true);
    try {
      await adminApi.saveStoreSettings({ settings });
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  // Reload iframe
  const reload = () => {
    if (iframeRef.current) {
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  const renderPanel = () => {
    if (!selected) return (
      <div className="edit-panel-empty">
        <FiLayout size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
        <p>Select a section</p>
        <p style={{ fontSize: '.8125rem', color: 'var(--text-muted)' }}>Click any section in the left panel or directly on the preview to edit it</p>
      </div>
    );
    const s = settings[selected] || {};
    const set = setSectionKey(selected);
    switch (selected) {
      case 'banners':    return <BannersPanel refreshIframe={reload} />;
      case 'logo':       return <LogoPanel s={s} set={set} />;
      case 'social':     return <SocialPanel s={s} set={set} settings={settings} setSettings={setSettings} />;
      case 'topBanner':  return <TopBannerPanel s={s} set={set} />;
      case 'ticker':     return <TickerPanel s={s} set={set} />;
      case 'nav':          return <NavPanel links={settings.navLinks || []} setLinks={setNavLinks} />;
      case 'featuredCats': return <FeaturedCatsPanel cats={settings.featuredCats || []} setCats={setFeaturedCats} />;
      case 'categories': return <CarouselCatsPanel cats={settings.carouselCats || []} setCats={setCarouselCats} />;
      case 'featured':       return <FeaturedProductsPanel s={s} set={set} refreshIframe={reload} />;
      case 'popularCategories':  return <PopularCategoriesPanel settings={settings} setSettings={setSettings} />;
      case 'categoryStrip':      return <CategoryStripPanel settings={settings} setSettings={setSettings} />;
      case 'homepageCategories': return <HomepageCategoriesPanel settings={settings} setSettings={setSettings} />;
      case 'midBanner':          return <MidBannerPanel s={s} set={set} settings={settings} setSettings={setSettings} />;
      default:               return null;
    }
  };

  if (loading) return <div className="admin-loading"><div className="admin-spinner" /><p>Loading editor…</p></div>;

  return (
    <div className="vs-editor">
      {/* ── Top toolbar ── */}
      <div className="vs-toolbar">
        <div className="vs-toolbar-left">
          <span className="vs-toolbar-title">Visual Page Editor</span>
          {dirty && <span className="vs-unsaved-dot" title="Unsaved changes" />}
        </div>
        <div className="vs-toolbar-center">
          <button
            className={`vs-viewport-btn ${viewport === 'desktop' ? 'active' : ''}`}
            onClick={() => setViewport('desktop')} title="Desktop"
          ><FiMonitor size={15} /></button>
          <button
            className={`vs-viewport-btn ${viewport === 'mobile' ? 'active' : ''}`}
            onClick={() => setViewport('mobile')} title="Mobile"
          ><FiSmartphone size={15} /></button>
        </div>
        <div className="vs-toolbar-right">
          <button className="btn-admin btn-admin-ghost btn-admin-sm" onClick={reload} title="Reload preview">
            <FiRefreshCw size={13} />
          </button>
          <button
            className="btn-admin btn-admin-primary"
            onClick={save}
            disabled={saving}
          >
            <FiSave size={14} />
            {saving ? 'Saving…' : saved ? '✓ Saved!' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* ── 3-panel body ── */}
      <div className="vs-body">
        {/* Left: section list */}
        <div className="vs-sections-panel">
          <div className="vs-sections-label">Page Sections</div>
          {SECTIONS.map(sec => (
            <button
              key={sec.id}
              className={`vs-section-btn ${selected === sec.id ? 'active' : ''}`}
              onClick={() => setSelected(s => s === sec.id ? null : sec.id)}
            >
              <span className="vs-section-icon">{sec.icon}</span>
              <span className="vs-section-text">
                <span className="vs-section-name">{sec.label}</span>
                <span className="vs-section-desc">{sec.desc}</span>
              </span>
              <FiChevronRight size={13} className="vs-section-arrow" />
            </button>
          ))}

          <div className="vs-sections-label" style={{ marginTop: 20 }}>How to use</div>
          <div className="vs-tip">
            Hover over any section in the preview to see it highlighted, then click to open its edit panel.
          </div>
        </div>

        {/* Center: iframe preview */}
        <div className="vs-preview-wrap">
          <div className={`vs-iframe-container ${viewport}`}>
            <div className="vs-browser-chrome">
              <div className="vs-chrome-dots">
                <span /><span /><span />
              </div>
              <div className="vs-chrome-url">{process.env.REACT_APP_FRONTEND_URL || 'localhost:3000'}</div>
            </div>
            {/* Measured wrapper — iframe rendered at full desktop width then scaled */}
            <div ref={previewRef} style={{ flex: 1, overflow: 'hidden', position: 'relative', minHeight: 0 }}>
              <iframe
                ref={iframeRef}
                src={process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000'}
                title="Store Preview"
                style={{
                  width: viewport === 'mobile' ? MOBILE_W : DESKTOP_W,
                  height: previewHeight,
                  transform: `scale(${previewScale})`,
                  transformOrigin: 'top left',
                  border: 'none',
                  display: 'block',
                  pointerEvents: 'auto',
                }}
                onLoad={() => pushPreview(settings)}
              />
            </div>
          </div>
        </div>

        {/* Right: edit panel */}
        <div className={`vs-edit-panel ${selected ? 'open' : ''}`}>
          {selected && (
            <div className="vs-edit-panel-header">
              <span>{SECTIONS.find(s => s.id === selected)?.label}</span>
              <button className="modal-close" onClick={() => setSelected(null)}><FiX /></button>
            </div>
          )}
          <div className="vs-edit-panel-body">
            {renderPanel()}
          </div>
        </div>
      </div>
    </div>
  );
}
